import { allGroups, findPane } from "../model/tree";
import { useRef, type CSSProperties, type PointerEvent } from "react";
import { equalizeGutter, resizeSplit } from "../model/ops";
import { layoutRects } from "../model/navigation";
import type { LayoutNode, LayoutTree, Split } from "../model/types";
import { cn } from "./cn";
import { useLayout } from "./LayoutContext";
import { PaneView, TabGroupView } from "./TabGroupView";

/** Stable siblings and DOM order preserve editors, focus and scroll through layout changes. */
export function NodeView({ width, height }: { width: number; height: number }) {
  const { tree, maximized, compact, registry } = useLayout();
  const groups = allGroups(tree);
  const rects = layoutRects(tree, width, height);
  const solo = maximized || compact;
  const focusedGroup = tree.focusedPaneId
    ? findPane(tree, tree.focusedPaneId)?.group.id
    : undefined;
  const gutters: Array<{ split: Split; index: number; style: CSSProperties; size: number }> = [];
  const visit = (node: LayoutNode) => {
    if (node.type !== "split") return;
    const parent = rects.get(node.id)!;
    const horizontal = node.direction === "horizontal";
    node.children.forEach((child, index) => {
      if (index > 0) {
        const rect = rects.get(child.id)!;
        gutters.push({
          split: node,
          index: index - 1,
          size: (horizontal ? parent.width : parent.height) - 4 * (node.children.length - 1),
          style: horizontal
            ? { left: rect.x - 4, top: parent.y, width: 4, height: parent.height }
            : { left: parent.x, top: rect.y - 4, width: parent.width, height: 4 },
        });
      }
      visit(child);
    });
  };
  visit(tree.root);
  const box = (id: string) => (solo ? { x: 0, y: 0, width, height } : rects.get(id)!);
  return (
    <>
      {groups.map((group) => {
        const rect = box(group.id);
        const hidden = solo && group.id !== focusedGroup;
        return (
          <div
            key={group.id}
            hidden={hidden}
            className={hidden ? "hidden" : "absolute flex"}
            style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height }}
          >
            <TabGroupView group={group} />
          </div>
        );
      })}
      {groups
        .flatMap((group) => group.tabs.map((pane) => ({ group, pane })))
        .sort((a, b) => a.pane.id.localeCompare(b.pane.id))
        .map(({ group, pane }) => {
          const rect = box(group.id);
          const hidden = pane.id !== group.activeTabId || (solo && group.id !== focusedGroup);
          return (
            <PaneView
              key={pane.id}
              pane={pane}
              focused={pane.id === tree.focusedPaneId}
              entry={registry[pane.paneType]}
              hidden={hidden}
              style={{
                left: rect.x,
                top: rect.y + 40,
                width: rect.width,
                height: Math.max(0, rect.height - 40),
              }}
            />
          );
        })}
      {!solo &&
        gutters.map((gutter) => <Gutter key={`${gutter.split.id}:${gutter.index}`} {...gutter} />)}
    </>
  );
}

type Drag = { start: number; size: number; tree: LayoutTree };

function Gutter({
  split,
  index,
  style,
  size,
}: {
  split: Split;
  index: number;
  style: CSSProperties;
  size: number;
}) {
  const { tree, onChange } = useLayout();
  const horizontal = split.direction === "horizontal";
  const dragRef = useRef<Drag | null>(null);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>): void => {
    if (event.button !== 0) return;
    if (size <= 0) return;
    dragRef.current = { start: horizontal ? event.clientX : event.clientY, size, tree };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>): void => {
    const drag = dragRef.current;
    if (!drag) return;
    const delta = (horizontal ? event.clientX : event.clientY) - drag.start;
    // Resize from the tree captured at drag start so a stale render cannot compound deltas.
    onChange(resizeSplit(drag.tree, split.id, index, delta, drag.size));
  };

  const endDrag = (event: PointerEvent<HTMLDivElement>): void => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  return (
    <div
      style={style}
      role="separator"
      aria-orientation={horizontal ? "vertical" : "horizontal"}
      data-layout-gutter={`${split.id}:${index}`}
      className={cn(
        "absolute z-20 shrink-0 bg-border transition-colors hover:bg-accent",
        horizontal ? "w-1 cursor-col-resize" : "h-1 cursor-row-resize",
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onDoubleClick={() => onChange(equalizeGutter(tree, split.id, index))}
    />
  );
}
